import { useState, useRef, useCallback } from "react";
import { Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { usePeople, type PersonWithCompany } from "@/hooks/usePeople";
import { useCreateComment } from "@/hooks/useComments";
import { cn } from "@/lib/utils";

interface CommentInputProps {
  entityType: string;
  entityId: string;
  parentId?: string;
  compact?: boolean;
  onCancel?: () => void;
  autoFocus?: boolean;
}

export function CommentInput({
  entityType,
  entityId,
  parentId,
  compact,
  onCancel,
  autoFocus,
}: CommentInputProps) {
  const [content, setContent] = useState("");
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [mentionedPersonIds, setMentionedPersonIds] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { data: people } = usePeople();
  const createComment = useCreateComment();
  const inputScope = parentId ?? "root";

  const filteredPeople =
    mentionQuery !== null
      ? (people || [])
          .filter((p) => {
            const name = `${p.first_name} ${p.last_name}`.toLowerCase();
            return name.includes(mentionQuery.toLowerCase());
          })
          .slice(0, 8)
      : [];

  const showDropdown = mentionQuery !== null && filteredPeople.length > 0;

  const handleInput = useCallback((value: string) => {
    setContent(value);

    const textarea = textareaRef.current;
    if (!textarea) return;

    // Auto-grow
    textarea.style.height = "auto";
    textarea.style.height = `${textarea.scrollHeight}px`;

    const cursorPos = textarea.selectionStart;
    const textBeforeCursor = value.slice(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@(\w*)$/);

    if (atMatch) {
      setMentionQuery(atMatch[1]);
    } else {
      setMentionQuery(null);
    }
  }, []);

  const insertMention = useCallback(
    (person: PersonWithCompany) => {
      const textarea = textareaRef.current;
      if (!textarea) return;

      const cursorPos = textarea.selectionStart;
      const textBeforeCursor = content.slice(0, cursorPos);
      const atIndex = textBeforeCursor.lastIndexOf("@");
      const textAfterCursor = content.slice(cursorPos);

      const mentionText = `@${person.first_name} ${person.last_name}`;
      const newContent =
        content.slice(0, atIndex) + mentionText + " " + textAfterCursor;

      setContent(newContent);
      setMentionQuery(null);

      if (!mentionedPersonIds.includes(person.id)) {
        setMentionedPersonIds([...mentionedPersonIds, person.id]);
      }

      setTimeout(() => {
        const newPos = atIndex + mentionText.length + 1;
        textarea.focus();
        textarea.setSelectionRange(newPos, newPos);
      }, 0);
    },
    [content, mentionedPersonIds],
  );

  const handleSubmit = useCallback(() => {
    const trimmed = content.trim();
    if (!trimmed) return;

    createComment.mutate(
      {
        entityType,
        entityId,
        content: trimmed,
        mentionedPersonIds,
        parentId,
      },
      {
        onSuccess: () => {
          setContent("");
          setMentionedPersonIds([]);
          if (textareaRef.current) {
            textareaRef.current.style.height = "auto";
          }
          onCancel?.();
        },
      },
    );
  }, [
    content,
    entityType,
    entityId,
    mentionedPersonIds,
    parentId,
    createComment,
    onCancel,
  ]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleSubmit();
    }
    if (e.key === "Escape") {
      if (mentionQuery !== null) {
        setMentionQuery(null);
      } else {
        onCancel?.();
      }
    }
  };

  return (
    <div className={cn("relative", compact ? "mt-2" : "border-t pt-3")}>
      <div className="relative">
        <Textarea
          ref={textareaRef}
          data-testid={`comment-input-${inputScope}`}
          value={content}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            compact ? "Write a reply..." : "Write a comment... Use @ to mention"
          }
          className={cn(
            "resize-none text-sm overflow-hidden",
            compact ? "min-h-[40px]" : "min-h-[60px]",
          )}
          rows={compact ? 1 : 2}
          autoFocus={autoFocus}
        />

        {showDropdown && (
          <div className="absolute bottom-full left-0 mb-1 w-56 rounded-md border bg-popover p-1 shadow-md z-50">
            {filteredPeople.map((person) => (
              <button
                key={person.id}
                className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
                onMouseDown={(e) => {
                  e.preventDefault();
                  insertMention(person);
                }}
              >
                <span className="font-medium">
                  {person.first_name} {person.last_name}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div
        className={cn(
          "flex items-center mt-2",
          compact ? "gap-2 justify-end" : "justify-end",
        )}
      >
        {compact && onCancel && (
          <Button variant="ghost" size="sm" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!content.trim() || createComment.isPending}
          data-testid={`comment-submit-${inputScope}`}
        >
          <Send className="h-3.5 w-3.5 mr-1.5" />
          {compact ? "Reply" : "Send"}
        </Button>
      </div>
    </div>
  );
}
